import { useEffect, useRef, useState } from 'react';

export const BACKEND_OTP_LENGTH = 6;
export const OTP_DURATION_SECONDS = 120;

function generateClientCode() {
    const random = new Uint32Array(1);
    const limit = Math.floor((2 ** 32) / 900000) * 900000;
    do {
        window.crypto.getRandomValues(random);
    } while (random[0] >= limit);
    return String(100000 + (random[0] % 900000));
}

function createChallenge() {
    return {
        code: generateClientCode(),
        expiresAt: Date.now() + OTP_DURATION_SECONDS * 1000
    };
}

export default function useOtpRegistration({ appName, onVerify, onVerified, onContinue }) {
    const [challenge, setChallenge] = useState(createChallenge);
    const challengeRef = useRef(challenge);
    const [remaining, setRemaining] = useState(OTP_DURATION_SECONDS);
    const [backendOtp, setBackendOtp] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const submissionPending = useRef(false);

    useEffect(() => {
        if (isVerified) {
            return;
        }
        const updateCountdown = () => {
            const current = challengeRef.current;
            const now = Date.now();
            if (now >= current.expiresAt) {
                let next = createChallenge();
                while (next.code === current.code) {
                    next = createChallenge();
                }
                challengeRef.current = next;
                setChallenge(next);
                setRemaining(OTP_DURATION_SECONDS);
                setBackendOtp('');
                setError('');
            } else {
                setRemaining(Math.ceil((current.expiresAt - now) / 1000));
            }
        };
        const timer = setInterval(updateCountdown, 1000);
        document.addEventListener('visibilitychange', updateCountdown);
        window.addEventListener('focus', updateCountdown);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', updateCountdown);
            window.removeEventListener('focus', updateCountdown);
        };
    }, [isVerified]);

    const updateBackendOtp = (value) => {
        if (submissionPending.current || isVerified) {
            return;
        }
        const nextOtp = value.replace(/[^0-9]/g, '').slice(0, BACKEND_OTP_LENGTH);
        setBackendOtp(nextOtp);
        setError('');
    };

    const submit = async (otpToSubmit = backendOtp) => {
        console.log('submitttinnnng');
        
        if (submissionPending.current || isVerified) {
            return;
        }
        if (otpToSubmit.length !== BACKEND_OTP_LENGTH) {
            setError('Enter the 6-digit backend OTP.');
            return;
        }
        const submittedChallenge = challengeRef.current;
        if (Date.now() >= submittedChallenge.expiresAt) {
            setError('Code expired. Wait for a new code and try again.');
            return;
        }
        if (!onVerify && !onContinue) {
            setError('OTP verification service is not connected.');
            return;
        }
        submissionPending.current = true;
        setIsSubmitting(true);
        setError('');
        try {
            if (!onVerify) {
                await onContinue(appName, submittedChallenge.code, otpToSubmit);
                return;
            }
            const verified = await onVerify(`${submittedChallenge.code}${otpToSubmit}`, appName);
            if (challengeRef.current !== submittedChallenge || Date.now() >= submittedChallenge.expiresAt) {
                setError('Code expired. Enter the OTP for the new code.');
                return;
            }
            if (verified === true) {
                setIsVerified(true);
                setBackendOtp('');
                if (onVerified) {
                    await onVerified(appName, submittedChallenge.code, otpToSubmit);
                }
            } else {
                setError('OTP verification failed. Check the code and try again.');
            }
        } catch (verificationError) {
            if (challengeRef.current === submittedChallenge) {
                const message = verificationError instanceof Error ? verificationError.message : '';
                if (message.toLowerCase().includes('invalid registration')) {
                    setBackendOtp('');
                }
                setError(onVerify ? 'Unable to verify the OTP. Please try again.' : message || 'Unable to open the application. Please try again.');
            }
        } finally {
            submissionPending.current = false;
            setIsSubmitting(false);
        }
    };

    let submitLabel = 'Verify OTP';
    let status = '';
    if (isSubmitting) {
        submitLabel = onVerify ? 'Verifying...' : 'Continuing...';
        status = submitLabel;
    } else if (isVerified) {
        submitLabel = 'Verified';
        status = 'Verification successful.';
    } else if (!onVerify && !onContinue) {
        status = 'OTP verification service is not connected.';
    }

    return {
        clientCode: challenge.code,
        remaining,
        backendOtp,
        error,
        isSubmitting,
        isVerified,
        isInputDisabled: isSubmitting || isVerified,
        canSubmit: backendOtp.length === BACKEND_OTP_LENGTH && !isSubmitting && !isVerified,
        status: error || status,
        submitLabel,
        updateBackendOtp,
        submit
    };
}